import cn from 'classnames'
import type { CSSProperties, ReactNode } from 'react'

import ReactTooltip from 'react-tooltip'

import './coreCheckbox.scss'

type CheckboxType = 'check' | 'dash' | 'circle' | 'checked-circle'

interface TooltipProps {
  id?: string
  text?: string
  textColor?: string
  backgroundColor?: string
  effect?: 'solid' | 'float'
  className?: string
  padding?: string
  place?: 'top' | 'bottom' | 'left' | 'right'
  enabled?: boolean
}

interface CheckboxProps {
  type: CheckboxType
  value: string
  label?: ReactNode
  checked?: boolean
  onClick?: (checked: boolean) => void
  fontStyle?: CSSProperties
  disabled?: boolean
  style?: CSSProperties
  className?: string
  optional?: any
  icon?: ReactNode
  dataTest?: string
  tooltip?: TooltipProps
  hint?: ReactNode
}

const Checkbox = ({
  type = 'check',
  value,
  label,
  onClick,
  checked = false,
  fontStyle,
  disabled,
  style,
  className,
  optional,
  dataTest,
  icon,
  tooltip,
  hint,
  ...props
}: CheckboxProps) => {
  const handleClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation()
    typeof onClick === 'function' && onClick(!checked)
  }

  // Generate unique tooltip ID if tooltip is enabled
  const tooltipId = tooltip?.enabled ? tooltip.id || `tooltip-${value}` : undefined

  return (
    <div
      data-test={dataTest || `check-button-${value}`}
      style={{ ...style }}
      className={cn(`${className}`, {
        'check-checkbox': type === 'check',
        'dash-checkbox': type === 'dash',
        'circle-checkbox': type === 'circle',
        'checked-circle-checkbox': type === 'checked-circle'
      })}
    >
      {/* Render tooltip only if enabled */}
      {tooltip?.enabled && (
        <ReactTooltip
          id={tooltipId}
          textColor={tooltip.textColor || 'black'}
          backgroundColor={tooltip.backgroundColor || 'white'}
          effect={tooltip.effect || 'solid'}
          className={tooltip.className || 'form-checkbox'}
          padding={tooltip.padding || '8px 6px'}
          place={tooltip.place || 'top'}
          positionStrategy='fixed'
        >
          {tooltip.text}
        </ReactTooltip>
      )}

      <input
        onChange={handleClick}
        disabled={disabled}
        checked={checked}
        value={value}
        id={value || ''}
        type='checkbox'
        data-tip={tooltip?.enabled ? tooltip.text : undefined}
        data-for={tooltip?.enabled ? tooltipId : undefined}
        {...props}
      />

      {label ? (
        <label
          htmlFor={value}
          data-tip={tooltip?.enabled ? tooltip.text : undefined}
          data-for={tooltip?.enabled ? tooltipId : undefined}
        >
          <span className='check-box-component'></span>
          {icon && <span className={'icon'}>{icon}</span>}
          <p data-test={`checkbox-label-${label}`} style={{ ...fontStyle }}>
            {label}
          </p>
          {Boolean(hint) && <span>{hint}</span>}
        </label>
      ) : (
        <label
          htmlFor={value}
          data-tip={tooltip?.enabled ? tooltip.text : undefined}
          data-for={tooltip?.enabled ? tooltipId : undefined}
        >
          <span className='check-box-component'></span>
        </label>
      )}

      {checked ? optional : null}
    </div>
  )
}

export default Checkbox
